import { Workbox } from 'workbox-window';

import { isFormDirty } from './dirty-form-registry';
import { setUpdateAvailable } from './update-available-store';

// Registers /sw.js (built by vite-plugin-pwa's injectManifest strategy from
// src/sw/sw.ts, see vite.config.ts) via workbox-window instead of the
// plugin's auto-injected register script — this module owns the update
// policy explicitly (ADR-017 / design decision D10):
//
//   - A worker already waiting BEFORE this registration (i.e. left over from
//     a previous visit, `wasWaitingBeforeRegister`) is safe to apply
//     automatically, UNLESS the valet is mid check-in (dirty-form-registry).
//   - A worker that starts waiting DURING this session is a fresh update
//     arriving while the app may be in use — never auto-applied. The
//     operator is offered it via UpdateAvailableToast and applies it with a
//     tap (see update-available-store.ts).
//
// Either way, skipWaiting is NEVER unconditional inside the SW itself — the
// SW only skips waiting on an explicit postMessage from here.
// Product bug fix: sw.ts calls `clients.claim()` on activate, so a FIRST
// install (no service worker controlling the page yet) takes control of the
// already-open page seconds after load — NOT just an update taking over
// from a previous version. The old unconditional
// `window.location.reload()` on 'controlling' reloaded on that first
// install too, wiping whatever the operator was mid-typing (an empty login
// form right after "Sign In", or an in-progress check-in on first visit
// after a deploy). Caught by the offline-chromium E2E's offline-reopen
// snapshot showing /login with empty fields after the test had filled them.
//
// workbox-window's `controlling` event carries `isUpdate` — true ONLY when
// a service worker was ALREADY controlling the page at the moment this
// Workbox instance called register() (see workbox-window's own
// `this._isUpdate = Boolean(navigator.serviceWorker.controller)`, set
// before registration). That is the correct "is this really an update"
// signal — not "something just started controlling".
//
// Exported for direct unit testing (service-worker.spec.ts) — kept a pure
// function so the reload decision is testable without mocking
// Workbox/window globals.
export function shouldReloadOnControlling(
  isUpdate: boolean | undefined,
  isDirty: boolean,
): boolean {
  return isUpdate === true && !isDirty;
}

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;

  const wb = new Workbox('/sw.js');

  wb.addEventListener('waiting', (event) => {
    const apply = () => wb.messageSkipWaiting();

    if (event.wasWaitingBeforeRegister && !isFormDirty()) {
      apply();
      return;
    }
    setUpdateAvailable(apply);
  });

  wb.addEventListener('controlling', (event) => {
    // If the form went dirty between skipWaiting() firing and this event
    // (a narrow race), the update is simply left for the next navigation —
    // never forced now. Never reload at all on a first install.
    if (shouldReloadOnControlling(event.isUpdate, isFormDirty())) {
      window.location.reload();
    }
  });

  window.addEventListener('load', () => {
    wb.register().catch(() => {
      // SW is optional in development.
    });
  });
}
